<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="$emit('close')">
    <div class="salary-modal">
      <div class="salary-modal-header">
        <div>
          <h2 class="salary-modal-title">Record Salary Expense - {{ monthLabel }}</h2>
          <p class="salary-modal-desc">Record salary as an expense and payable. The amount will remain pending until paid.</p>
        </div>
        <button class="close-button" @click="$emit('close')">&times;</button>
      </div>

      <div class="salary-modal-body">
        <div class="modal-top-row">
          <div class="modal-field">
            <label class="field-label">Expense Date</label>
            <div class="date-display">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <input v-model="expenseDate" type="date" class="date-input">
            </div>
          </div>
          <div class="modal-summary">
            <p class="summary-label">Total Payable</p>
            <p class="summary-value">{{ formatCurrency(totalPayable) }}</p>
            <p class="summary-sub">{{ selectedEmployees.length }} employees selected</p>
          </div>
        </div>

        <div v-if="employeesLoading" class="loading-box">Loading employees...</div>
        <div v-else-if="employeeList.length === 0" class="loading-box">No employees found</div>
        <div v-else class="modal-table-container">
          <table class="modal-table">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <input type="checkbox" :checked="selectedEmployees.length === employeeList.length && employeeList.length > 0" @change="toggleAll">
                </th>
                <th>Employee</th>
                <th>Gross Salary</th>
                <th>Deductions</th>
                <th>Net Payable</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in employeeList" :key="emp.partyID">
                <td class="checkbox-col">
                  <input type="checkbox" :checked="selectedEmployees.includes(emp.partyID)" @change="toggleEmployee(emp.partyID)">
                </td>
                <td><span class="employee-name">{{ emp.fullName || emp.name }}</span></td>
                <td>
                  <input type="number" class="inline-input" :value="getGrossSalary(emp.partyID)" @input="setGrossSalary(emp.partyID, $event)" min="0.01" step="0.01">
                </td>
                <td>
                  <input type="number" class="inline-input" :value="getDeductions(emp.partyID)" @input="setDeductions(emp.partyID, $event)" min="0" step="0.01">
                </td>
                <td class="net-col">{{ formatCurrency(getGrossSalary(emp.partyID) - getDeductions(emp.partyID)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="salary-modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Cancel</button>
        <button class="btn-primary" @click="handleSubmit" :disabled="submitting">
          {{ submitting ? 'Recording...' : `Record Expense (${selectedEmployees.length} employees)` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'RecordSalaryExpenseModal',
  props: {
    isOpen: { type: Boolean, required: true },
    monthLabel: { type: String, required: true },
    month: { type: Number, required: true },
    year: { type: Number, required: true }
  },
  data () {
    return {
      expenseDate: new Date().toISOString().split('T')[0],
      selectedEmployees: [],
      salaryAmounts: {},
      deductionAmounts: {},
      submitting: false
    }
  },
  computed: {
    ...mapGetters('employee', { employeeData: 'employees', employeesLoading: 'loading' }),
    employeeList () {
      return this.employeeData?.employees || []
    },
    totalPayable () {
      return this.selectedEmployees.reduce((sum, id) => {
        return sum + (this.getGrossSalary(id) - this.getDeductions(id))
      }, 0)
    }
  },
  watch: {
    isOpen (val) {
      if (val) {
        this.fetchEmployees()
        this.resetForm()
      }
    },
    employeeList: {
      handler (list) {
        if (list.length > 0 && this.selectedEmployees.length === 0) {
          this.selectedEmployees = list.map(e => e.partyID)
          list.forEach(e => {
            this.$set(this.salaryAmounts, e.partyID, e.basicSalary || 0)
            this.$set(this.deductionAmounts, e.partyID, 0)
          })
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('employee', ['fetchEmployees']),
    ...mapActions('salary', ['recordSalaryExpense']),

    getGrossSalary (id) {
      return this.salaryAmounts[id] || 0
    },
    getDeductions (id) {
      return this.deductionAmounts[id] || 0
    },
    setGrossSalary (id, event) {
      this.$set(this.salaryAmounts, id, parseFloat(event.target.value) || 0)
    },
    setDeductions (id, event) {
      this.$set(this.deductionAmounts, id, parseFloat(event.target.value) || 0)
    },

    toggleEmployee (id) {
      const idx = this.selectedEmployees.indexOf(id)
      if (idx === -1) this.selectedEmployees.push(id)
      else this.selectedEmployees.splice(idx, 1)
    },
    toggleAll () {
      if (this.selectedEmployees.length === this.employeeList.length) {
        this.selectedEmployees = []
      } else {
        this.selectedEmployees = this.employeeList.map(e => e.partyID)
      }
    },

    async handleSubmit () {
      if (this.selectedEmployees.length === 0) {
        this.$store.dispatch('notification/showError', 'Please select at least one employee')
        return
      }

      this.submitting = true
      try {
        const date = new Date(this.expenseDate)
        const payload = {
          expenseDate: date.toISOString(),
          month: date.getMonth() + 1,
          year: date.getFullYear(),
          employees: this.selectedEmployees.map(id => ({
            employeePartyID: id,
            grossSalary: this.getGrossSalary(id),
            deductions: this.getDeductions(id)
          }))
        }
        await this.recordSalaryExpense(payload)
        this.$emit('recorded')
        this.$emit('close')
      } catch (e) {
        // error handled in store
      } finally {
        this.submitting = false
      }
    },

    resetForm () {
      this.expenseDate = new Date().toISOString().split('T')[0]
      this.selectedEmployees = []
      this.salaryAmounts = {}
      this.deductionAmounts = {}
    },

    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.salary-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}

.salary-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 28px 0;
}

.salary-modal-title { font-size: 20px; font-weight: 700; color: #1f2937; margin: 0 0 6px; }
.salary-modal-desc { font-size: 14px; color: #6b7280; margin: 0; }

.close-button {
  background: none; border: none; font-size: 28px;
  color: #6b7280; cursor: pointer; padding: 0; line-height: 1;
}

.salary-modal-body { padding: 24px 28px; }

.modal-top-row {
  display: flex; align-items: flex-end; gap: 24px;
  margin-bottom: 24px; flex-wrap: wrap;
}

.modal-field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 14px; font-weight: 500; color: #374151; }

.date-display {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border: 1px solid #d1d5db;
  border-radius: 8px; background: white;
}

.date-input { border: none; font-size: 14px; color: #1f2937; background: transparent; outline: none; }

.modal-summary { margin-left: auto; text-align: right; }
.summary-label { font-size: 13px; color: #6b7280; margin: 0 0 2px; }
.summary-value { font-size: 24px; font-weight: 700; color: #1f2937; margin: 0; }
.summary-sub { font-size: 13px; color: #6b7280; margin: 2px 0 0; }

.loading-box {
  padding: 40px 20px; text-align: center;
  color: #6b7280; background: #f9fafb; border-radius: 8px;
}

.modal-table-container { overflow-x: auto; }
.modal-table { width: 100%; border-collapse: collapse; }

.modal-table th {
  padding: 12px; text-align: left; font-size: 13px;
  font-weight: 600; color: #6b7280; background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.modal-table td {
  padding: 12px; font-size: 14px; color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
}

.checkbox-col { width: 40px; text-align: center; }
.employee-name { font-weight: 600; }
.net-col { font-weight: 700; text-align: right; }

.inline-input {
  width: 90px; padding: 6px 10px; border: 1px solid #d1d5db;
  border-radius: 6px; font-size: 14px; color: #1f2937; text-align: right;
}

.inline-input:focus {
  outline: none; border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

input[type="checkbox"] { cursor: pointer; }

.salary-modal-footer {
  display: flex; justify-content: center; gap: 12px;
  padding: 20px 28px; border-top: 1px solid #e5e7eb;
}

.btn-cancel {
  padding: 10px 24px; border: 1px solid #e5e7eb; background: white;
  color: #1f2937; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
}

.btn-cancel:hover { background: #f3f4f6; }

.btn-primary {
  background: #2563eb; color: white; border: none; padding: 10px 24px;
  border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
}

.btn-primary:hover { background: #1d4ed8; }
.btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

@media (max-width: 768px) {
  .modal-top-row { flex-direction: column; align-items: stretch; }
  .modal-summary { margin-left: 0; text-align: left; }
}
</style>
